// 「진료 후 질문」(질문 로드맵 엔진) 노출 공용 훅 — 파일럿 치과 한정 (2026-10-06, 법무 P0: 업무약관 v7.0 시행 11-06 전 일반 오픈 금지)
//
// 노출 = isRoadmapEngineOn(config/features.roadmapEngine strict + 환자관리 열림)  AND  서버 getPatientAppClinicConsent 응답이
//        isPilot === true && confirmed && version ∈ ROADMAP_PA_CLINIC_VERSIONS(pa-clinic-1.2·1.3 — 2026-10-11)
// fail-closed: 응답 전·조회 실패·구 서버(isPilot 키 없음)·치과 아닌 계정 = 숨김. 플래그 OFF면 서버 호출 0(기존 원칙 유지).
// 치과 업체 계정(대표·직원)만 호출 — 직원은 서버가 소속 대표 치과의 확인 상태로 판정한다.
// 호출 횟수: getPatientAppClinicConsentShared 가 30초 공유 캐시 + 진행 중 Promise 를 재사용하므로 위탁 확인 카드와 같은 화면에 있어도 네트워크 1회.
// 반환 { on, loading, flagOn, pilotOk } — 화면은 on 만 보면 된다. ClinicRoadmapSettings 는 flagOn/pilotOk 로 안내 문구를 가른다.
import { useEffect, useState } from 'react';
import { getFeatures, isRoadmapEngineOn } from './features';
import { getPatientAppClinicConsentShared, subscribePatientAppClinicConsent } from '../services/careAppApi';
import { CARE_PA_CLINIC } from '../config/careLegal';

export const CLINIC_TYPES = ['clinic', 'dental', '치과'];

export function isClinicAccount(user) {
    return CLINIC_TYPES.includes(user?.businessType || user?.companyBusinessType);
}

// 진료 후 질문을 인정하는 위탁 확인 판 — 서버 ROADMAP_PA_CLINIC_VERSIONS 와 같아야 한다(계획_케어앱_예약대기 S8: 1.3 게시 때 서버도 1.2·1.3 둘 다 인정).
//   2026-10-11 검토 지적: 종전 "version === CARE_PA_CLINIC.version && isCurrent" 정확 일치는 1.3 게시 순간 1.2 확인 치과의 화면만 숨기고 서버는 1.2로 계속 발송하는 어긋남을 만든다.
//   지금(1.3 미게시)은 1.2 확인 = isCurrent 이므로 동작 불변. 1.1 은 서버도 로드맵을 인정하지 않으므로 넣지 않는다.
export const ROADMAP_PA_CLINIC_VERSIONS = Object.freeze(['pa-clinic-1.2', 'pa-clinic-1.3']);
void CARE_PA_CLINIC;   // 현재 판 상수 — 판 표를 바꿀 때 대조용(import 보존)

// 서버 응답(normalizeConsentState 결과) → 파일럿 판정. 어느 하나라도 못 미치면 false
export function isRoadmapPilotConsent(d) {
    return !!d && d.isPilot === true && d.confirmed === true && ROADMAP_PA_CLINIC_VERSIONS.includes(d.version);
}

export function useRoadmapPilot(user) {
    const [state, setState] = useState({ on: false, loading: true, flagOn: false, pilotOk: false });
    const uid = user?.uid || '';
    const email = user?.email || '';
    const isClinic = isClinicAccount(user);

    useEffect(() => {
        let alive = true;
        if (!uid || !isClinic) { setState({ on: false, loading: false, flagOn: false, pilotOk: false }); return () => { alive = false; }; }
        setState(s => ({ ...s, loading: true }));

        const run = async () => {
            let flagOn = false;
            try { flagOn = isRoadmapEngineOn(await getFeatures(), user, email); }
            catch (e) { console.error('[진료 후 질문 게이트] 플래그 조회 실패:', e?.code || e?.message || e); }
            if (!alive) return;
            if (!flagOn) { setState({ on: false, loading: false, flagOn: false, pilotOk: false }); return; }
            let pilotOk = false;
            try { pilotOk = isRoadmapPilotConsent(await getPatientAppClinicConsentShared()); }
            catch (e) { console.error('[진료 후 질문 게이트] 위탁 확인 조회 실패(숨김 처리):', e?.code || e?.details?.code || e?.message || e); }
            if (!alive) return;
            setState({ on: pilotOk, loading: false, flagOn: true, pilotOk });
        };
        run();
        // 위탁 확인 카드에서 확인·철회하면 즉시 재판정
        const unsub = subscribePatientAppClinicConsent(() => { if (alive) run(); });
        return () => { alive = false; unsub(); };
    }, [uid, email, isClinic]); // eslint-disable-line react-hooks/exhaustive-deps

    return state;
}

export default useRoadmapPilot;
